/* Customer Ordering Dashboard — place a new order.

   v3.6r · Now backed by `POST /api/portal/quote` for live pricing:
   pulls the customer's contract price per m³ AND live delivery km +
   surcharge ($2/km beyond 25 km) from the server so the total the
   customer sees is exactly what they'll be invoiced. For account
   customers the job lands on Dispatch with `status=pending_approval`;
   for cash customers it becomes a QuoteRequest for staff review. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShoppingCart, CheckCircle2, Truck, MapPin } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PortalPlaceOrder() {
  const { user } = usePortalAuth();
  const nav = useNavigate();
  const [mixes, setMixes] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteMode, setSiteMode] = useState("saved");   // 'saved' | 'new'
  const [form, setForm] = useState({
    mix_id: "",
    quantity_m3: 8,
    date: new Date().toISOString().slice(0, 10),
    time_required: "09:00",
    site_id: "",
    site_name: "",
    site_address: "",
    po_number: "",
    notes: "",
    slump_mm: "",
  });
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const quoteToken = useRef(0);
  // v3.6z — Shrinkage-crack risk state. Reset whenever mix/date/time/site
  // changes so the customer must re-accept if conditions change.
  const [riskAccepted, setRiskAccepted] = useState(false);
  const [showRiskModal, setShowRiskModal] = useState(false);
  useEffect(() => { setRiskAccepted(false); setShowRiskModal(false); }, [form.mix_id, form.date, form.time_required, form.site_id, form.site_address]);

  useEffect(() => {
    (async () => {
      try {
        const [mixesR, meR] = await Promise.all([
          api.get("/portal/mixes"),
          api.get("/portal/auth/me"),
        ]);
        setMixes(mixesR.data);
        const savedSites = (meR.data?.sites || []).filter((s) => s.address || s.name);
        setSites(savedSites);
        setSiteMode(savedSites.length ? "saved" : "new");
      } catch (e) { console.warn("portal place-order bootstrap failed", e); }
    })();
  }, []);

  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });
  const selectedMix = useMemo(() => mixes.find((m) => m.id === form.mix_id), [mixes, form.mix_id]);

  // Debounced live quote — recalculates whenever the mix, quantity, or site
  // changes. Cancels any inflight request so late responses can't overwrite
  // fresh input.
  useEffect(() => {
    const qty = Number(form.quantity_m3);
    if (!form.mix_id || !qty || qty <= 0) { setQuote(null); setQuoteError(null); return; }
    if (!form.site_id && !form.site_address.trim()) { setQuote(null); setQuoteError(null); return; }
    const my = ++quoteToken.current;
    const t = setTimeout(async () => {
      setQuoteLoading(true);
      setQuoteError(null);
      try {
        const { data } = await api.post("/portal/quote", {
          mix_id: form.mix_id,
          quantity_m3: qty,
          site_id: form.site_id || null,
          site_address: form.site_address || null,
          requested_date: form.date,
          requested_time: form.time_required || null,
        });
        if (my === quoteToken.current) setQuote(data);
      } catch (e) {
        if (my === quoteToken.current) {
          setQuote(null);
          setQuoteError(e.response?.data?.detail || "Quote unavailable");
        }
      } finally {
        if (my === quoteToken.current) setQuoteLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.mix_id, form.quantity_m3, form.site_id, form.site_address, form.date, form.time_required]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.mix_id) return toast.error("Choose a mix");
    if (!form.quantity_m3 || form.quantity_m3 <= 0) return toast.error("Enter a quantity");
    if (!form.site_address && !form.site_id) return toast.error("Enter a delivery site");
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity_m3: Number(form.quantity_m3),
        slump_mm: form.slump_mm ? Number(form.slump_mm) : null,
        // Advisory only — the warning is shown, never enforced. Recorded for the audit trail.
        risk_accepted: !!(quote?.risk?.has_risk && riskAccepted),
        risk_shown: !!quote?.risk?.has_risk,
        risk_reasons: quote?.risk?.reasons || [],
        risk_weather: quote?.risk?.weather || null,
      };
      const r = await api.post("/portal/orders", payload);
      setConfirmation(r.data);
      toast.success(
        r.data.kind === "job"
          ? "Order submitted · pending dispatch approval"
          : "Quote request submitted for review"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Order failed");
    } finally {
      setBusy(false);
    }
  };

  const pickSite = (sid) => {
    const s = sites.find((x) => x.id === sid);
    if (!s) return;
    setForm({ ...form, site_id: s.id, site_name: s.name, site_address: s.address });
  };

  if (confirmation) {
    return (
      <div className="max-w-2xl" data-testid="portal-order-confirmed">
        <div className="bg-white border border-neutral-200 p-8 shadow-sm">
          <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded flex items-center justify-center mb-5">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <div className="text-[10px] tracking-widest uppercase text-neutral-500">Confirmed</div>
          <h1 className="text-3xl font-black mt-1 mb-3">
            {confirmation.kind === "job" ? "Order Requested" : "Quote Requested"}
          </h1>
          <p className="text-neutral-600 leading-relaxed">
            {confirmation.kind === "job"
              ? "Thanks — your order is with dispatch for approval. You'll get an email confirmation and can track its status under Deliveries."
              : "Dispatch will review your request and reply with a firm quote. You can check its status under Quotes."}
          </p>
          <div className="flex gap-3 mt-6">
            <Button data-testid="portal-order-view-jobs" onClick={() => nav(confirmation.kind === "job" ? "/portal/jobs" : "/portal/quotes")} className="rounded-none bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] uppercase text-xs tracking-widest font-bold">
              {confirmation.kind === "job" ? "View my deliveries" : "View my quotes"}
            </Button>
            <Button variant="outline" onClick={() => {
              setConfirmation(null);
              setQuote(null);
              setForm({ ...form, quantity_m3: 8, po_number: "", notes: "" });
            }} className="rounded-none border-neutral-300 uppercase text-xs tracking-widest font-bold">
              Place another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isAccount = user?.customer_type === "account";
  const surchargeApplies = quote && quote.delivery_kind === "surcharge";

  return (
    <div className="max-w-5xl pb-24 lg:pb-6" data-testid="portal-place-order">
      <div className="mb-4">
        <div className="text-[10px] tracking-widest uppercase text-neutral-500">Place Order</div>
        <h1 className="text-2xl font-black text-neutral-900">Request a delivery</h1>
        <p className="text-neutral-500 text-sm">
          {isAccount
            ? "Your contract price loads automatically. Distance beyond 25 km adds a small per-km surcharge — you'll see the full total before you hit Request."
            : "Your request goes to dispatch for review — they'll come back with a firm quote."}
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-neutral-200 p-4 lg:p-5 space-y-3 shadow-sm">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Mix</Label>
            <Select value={form.mix_id} onValueChange={set("mix_id")}>
              <SelectTrigger data-testid="order-mix" className="rounded-none border-neutral-300 h-9">
                <SelectValue placeholder="Choose a mix" />
              </SelectTrigger>
              <SelectContent>
                {mixes.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.code} · {m.name} · ${m.price_per_m3}/m³
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Quantity (m³)</Label>
            <div className="flex items-stretch">
              <button type="button" data-testid="order-qty-minus" aria-label="Less 0.2 m³"
                onClick={() => setForm({ ...form, quantity_m3: Math.max(0.2, Math.round((Number(form.quantity_m3 || 0) - 0.2) * 10) / 10) })}
                className="w-10 border border-r-0 border-neutral-300 mono font-black text-lg hover:bg-neutral-100 active:bg-neutral-200">−</button>
              <Input data-testid="order-qty" type="number" step="0.2" min="0.2" value={form.quantity_m3} onChange={set("quantity_m3")}
                onBlur={() => { const q = Math.round(Number(form.quantity_m3 || 0) * 10) / 10; if (q !== form.quantity_m3) setForm({ ...form, quantity_m3: q }); }}
                className="rounded-none border-neutral-300 mono h-9 text-center font-bold" />
              <button type="button" data-testid="order-qty-plus" aria-label="More 0.2 m³"
                onClick={() => setForm({ ...form, quantity_m3: Math.round((Number(form.quantity_m3 || 0) + 0.2) * 10) / 10 })}
                className="w-10 border border-l-0 border-neutral-300 mono font-black text-lg hover:bg-neutral-100 active:bg-neutral-200">+</button>
            </div>
            <div className="mono text-[10px] text-neutral-400 mt-1">Steps of 0.2 m³ — or type any amount</div>
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Delivery date</Label>
            <Input data-testid="order-date" type="date" value={form.date} onChange={set("date")} className="rounded-none border-neutral-300 mono h-9" />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Time on site</Label>
            <Input data-testid="order-time" type="time" value={form.time_required} onChange={set("time_required")} className="rounded-none border-neutral-300 mono h-9" />
          </div>

          {/* Delivery site — saved dropdown OR type a new address */}
          <div className="md:col-span-2">
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Delivery Site
            </Label>
            {sites.length > 0 && (
              <div className="flex items-center gap-1.5 border border-neutral-200 p-1 w-fit bg-neutral-50 mb-2">
                <button
                  type="button"
                  data-testid="order-site-mode-saved"
                  onClick={() => setSiteMode("saved")}
                  className={`px-3 py-1 text-[10px] uppercase tracking-widest ${
                    siteMode === "saved" ? "bg-primary-orange text-black font-bold" : "text-neutral-500 hover:text-primary-orange"
                  }`}
                >Saved sites</button>
                <button
                  type="button"
                  data-testid="order-site-mode-new"
                  onClick={() => { setSiteMode("new"); setForm({ ...form, site_id: "" }); }}
                  className={`px-3 py-1 text-[10px] uppercase tracking-widest ${
                    siteMode === "new" ? "bg-primary-orange text-black font-bold" : "text-neutral-500 hover:text-primary-orange"
                  }`}
                >New address</button>
              </div>
            )}
            {siteMode === "saved" && sites.length > 0 ? (
              <Select value={form.site_id} onValueChange={pickSite}>
                <SelectTrigger data-testid="order-site-saved" className="rounded-none border-neutral-300 h-9">
                  <SelectValue placeholder="Pick a saved site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name ? `${s.name} · ` : ""}{s.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="grid gap-2">
                <Input data-testid="order-site-name" placeholder="Site name / reference (optional)" value={form.site_name} onChange={set("site_name")} className="rounded-none border-neutral-300 h-9" />
                <Input data-testid="order-site-address" placeholder="Full delivery address" value={form.site_address} onChange={set("site_address")} className="rounded-none border-neutral-300 h-9" />
              </div>
            )}
          </div>

          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">PO number (optional)</Label>
            <Input data-testid="order-po" value={form.po_number} onChange={set("po_number")} className="rounded-none border-neutral-300 mono h-9" />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Slump (mm, optional)</Label>
            <Input data-testid="order-slump" type="number" placeholder={selectedMix?.slump_mm || ""} value={form.slump_mm} onChange={set("slump_mm")} className="rounded-none border-neutral-300 mono h-9" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Notes for dispatch</Label>
            <Textarea data-testid="order-notes" rows={2} value={form.notes} onChange={set("notes")} className="rounded-none border-neutral-300" />
          </div>
        </div>

        {/* Live quote panel — Volume · Base · Distance · Total */}
        <div className="border-2 border-neutral-900 bg-white p-4 mt-3" data-testid="order-quote-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-widest uppercase text-neutral-500 flex items-center gap-1">
              <Truck className="w-3 h-3" />
              Live Quote
              {quoteLoading && <span className="text-primary-orange ml-2 animate-pulse">calculating…</span>}
            </div>
            {quote && (
              <div className="text-[10px] mono text-neutral-500">
                @ {money(quote.price_per_m3)}/m³ contract rate
              </div>
            )}
          </div>

          {!quote && !quoteLoading && (
            <div className="text-sm text-neutral-400 py-6 text-center">
              {quoteError
                ? quoteError
                : "Pick a mix, enter a quantity and a delivery site to see your total."}
            </div>
          )}

          {quote && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <QuoteCell
                  label="Volume"
                  value={`${quote.quantity_m3} m³`}
                  subtext={quote.mix?.code}
                  testId="quote-volume"
                />
                <QuoteCell
                  label="Base Price"
                  value={money(quote.base_price)}
                  subtext={`${quote.quantity_m3} × ${money(quote.price_per_m3)}`}
                  testId="quote-base"
                />
                <QuoteCell
                  label="Distance Surcharge"
                  value={money(quote.delivery_charge)}
                  subtext={
                    quote.delivery_km === null
                      ? "Address not resolved yet"
                      : surchargeApplies
                        ? `${quote.delivery_km.toFixed(1)} km · $${quote.delivery_rate_per_km}/km over ${quote.delivery_free_km} km`
                        : `${quote.delivery_km.toFixed(1)} km · free within ${quote.delivery_free_km} km`
                  }
                  tone={surchargeApplies ? "warn" : "muted"}
                  testId="quote-surcharge"
                />
                <QuoteCell
                  label="Total inc. GST"
                  value={money(quote.total)}
                  subtext={`Subtotal ${money(quote.subtotal)} · GST ${money(quote.gst)}`}
                  tone="big"
                  testId="quote-total"
                />
              </div>
              {quote.small_load_charge > 0 && (
                <div className="text-[10px] text-amber-600 mt-2 mono">
                  Includes {money(quote.small_load_charge)} small-load top-up (min {quote.small_load_charge > 0 && quote.quantity_m3} m³).
                </div>
              )}
              {/* v3.6z — Shrinkage-crack risk banner */}
              {quote.risk?.has_risk && (
                <button
                  type="button"
                  onClick={() => setShowRiskModal(true)}
                  data-testid="risk-banner"
                  className={`w-full mt-3 border-2 p-3 text-left transition ${
                    riskAccepted
                      ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
                      : "border-red-500 bg-red-50 hover:bg-red-100"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`mono text-[10px] uppercase tracking-widest font-bold ${riskAccepted ? "text-amber-700" : "text-red-700"}`}>
                      ⚠ Shrinkage-Crack Warning · Tap for details
                    </span>
                  </div>
                  <div className="mono text-[11px] mt-1 text-neutral-700">
                    {quote.risk.reasons?.join(" · ")}
                  </div>
                  {quote.risk.weather && (
                    <div className="mono text-[10px] mt-1 text-neutral-500">
                      Forecast @ {quote.risk.weather.at?.slice(11, 16) || form.time_required || "09:00"} · {quote.risk.weather.temperature_c ?? "—"}°C · {quote.risk.weather.humidity_pct ?? "—"}% humidity · {quote.risk.weather.wind_kmh ?? "—"} km/h wind
                    </div>
                  )}
                </button>
              )}
            </>
          )}
        </div>

        {/* v3.6z — Shrinkage-crack risk acceptance modal */}
        {showRiskModal && quote?.risk?.has_risk && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="risk-modal">
            <div className="bg-white max-w-lg w-full p-6 border-4 border-red-600">
              <div className="mono text-[11px] uppercase tracking-widest text-red-700 font-black">Weather Advisory</div>
              <div className="text-xl font-black text-neutral-900 mt-1">Plastic Shrinkage Crack Risk</div>
              <p className="text-[13px] text-neutral-800 mt-3 leading-relaxed">
                <b>CAUTION:</b> Current weather conditions ({quote.risk.reasons?.join(", ")}) significantly increase
                the risk of Plastic Shrinkage Cracking. We recommend using an evaporation retarder or postponing the
                pour.
              </p>
              {quote.risk.weather && (
                <div className="mt-3 grid grid-cols-3 gap-2 mono text-xs">
                  <div className="border border-red-300 bg-red-50 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-red-600">Temp</div>
                    <div className="font-black text-lg">{quote.risk.weather.temperature_c ?? "—"}°C</div>
                  </div>
                  <div className="border border-red-300 bg-red-50 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-red-600">Humidity</div>
                    <div className="font-black text-lg">{quote.risk.weather.humidity_pct ?? "—"}%</div>
                  </div>
                  <div className="border border-red-300 bg-red-50 p-2">
                    <div className="text-[9px] uppercase tracking-widest text-red-600">Wind</div>
                    <div className="font-black text-lg">{quote.risk.weather.wind_kmh ?? "—"} km/h</div>
                  </div>
                </div>
              )}
              <div className="mt-5 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => { setRiskAccepted(true); setShowRiskModal(false); }}
                  data-testid="risk-accept-btn"
                  className="w-full rounded-none bg-neutral-900 hover:bg-neutral-800 text-white uppercase tracking-widest text-sm font-black py-3"
                >
                  Got it — continue with my order
                </button>
                <button
                  type="button"
                  onClick={() => setShowRiskModal(false)}
                  data-testid="risk-cancel-btn"
                  className="w-full rounded-none border border-neutral-400 bg-white hover:bg-neutral-50 uppercase tracking-widest text-xs text-neutral-700 py-2"
                >
                  Close · I'll reconsider the pour time
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Desktop submit */}
        <div className="hidden lg:flex justify-end pt-1">
          <Button data-testid="order-submit" type="submit" disabled={busy} className="rounded-none bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-sm font-bold px-6 py-2.5">
            <ShoppingCart className="w-4 h-4 mr-2" />
            {busy
              ? "Submitting…"
              : isAccount
                ? "Request Order Slot"
                : "Submit request"}
          </Button>
        </div>

        {/* Mobile / small-screen sticky submit */}
        <div className="lg:hidden fixed left-0 right-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-6px_16px_rgba(0,0,0,0.08)]">
          <Button data-testid="order-submit-mobile" type="submit" disabled={busy} className="w-full rounded-none bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-sm font-bold py-3">
            <ShoppingCart className="w-4 h-4 mr-2" />
            {busy
              ? "Submitting…"
              : isAccount
                ? "Request Order Slot"
                : "Submit request"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function QuoteCell({ label, value, subtext, tone, testId }) {
  const toneCls = {
    big:   "border-primary-orange bg-primary-orange/5",
    warn:  "border-amber-400 bg-amber-50",
    muted: "border-neutral-200 bg-neutral-50",
  }[tone] || "border-neutral-200 bg-neutral-50";
  const valueCls = tone === "big"
    ? "mono font-black text-2xl text-primary-orange"
    : "mono font-black text-lg text-neutral-900";
  return (
    <div className={`border ${toneCls} p-3`} data-testid={testId}>
      <div className="text-[10px] tracking-widest uppercase text-neutral-500">{label}</div>
      <div className={`${valueCls} leading-none mt-1`}>{value}</div>
      {subtext && <div className="text-[10px] text-neutral-500 mt-1 mono">{subtext}</div>}
    </div>
  );
}
